import { ComponentProps, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { AnimatePresence, motion } from 'framer-motion'
import dayjs from 'dayjs'

import { FieldGroup, ToastSeverity, useToast } from '@/ui'
import {
  BirthdayCakeOutline,
  Button,
  Callout,
  Checkbox,
  FormErrorMessage,
  Input,
  InputDate,
  PersonOutline,
  Skeleton,
  getMotionProps,
} from '@dashboard/ui'
import { SelectJurisdiction } from '@/layouts/creator/components'
import { creatorCreateTermsMutation } from '@/layouts/creator/api'
import { Country, CreatorTermsCreate, UnitedStates } from '@/api/dashboard'
import { getErrorText } from '@/api'
import { ErrorMessage } from '@hookform/error-message'
import { useDebounce } from '@/hooks'
import { useCreatorLegality } from '@/layouts/creator/hooks'
import { useCreatorToken } from '@/layouts/creator/provider'

const [CREATOR_PROGRAM_TERMS, CREATOR_PROGRAM_FOR_MINORS_TERMS] = [
  'https://aghanim.com/creator-program',
  'https://aghanim.com/creator-program-for-minors',
]

const DATES = { min: dayjs().subtract(100, 'year').toDate(), max: dayjs().subtract(1, 'year').toDate() }

const slideMotionProps = getMotionProps({
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
})

export const CreatorAcceptTermsForm = () => {
  const { token } = useCreatorToken()
  const { t } = useTranslation()
  const [confirmed, setConfirmed] = useState<boolean>(false)
  const showToast = useToast()
  const { mutateAsync: accept } = creatorCreateTermsMutation(token)

  const {
    watch,
    control,
    trigger,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreatorTermsCreate>()
  const [name, dob, country, state] = watch(['legal_name', 'date_of_birth', 'country', 'state'])
  const debounced = {
    name: useDebounce(name, 500),
  }

  const fulfilled = name && dob && country

  const handleAccept = handleSubmit(async data => {
    try {
      const dob = dayjs(data.date_of_birth).format('YYYY-MM-DD')
      await accept({ ...data, date_of_birth: dob })
    } catch (e) {
      showToast({ message: getErrorText(e), severity: ToastSeverity.error })
    }
  })

  const submitDisabled = !fulfilled || !confirmed

  return (
    <form onSubmit={handleAccept} className="flex flex-col">
      <FieldGroup
        size="sm"
        label={<span className="text-caption-sm text-text-tertiary">{t('creator.terms.legal-name')}</span>}
      >
        <Input
          {...register('legal_name', { required: t('validation.required'), setValueAs: v => v.trim() })}
          extraLeft={{ icon: PersonOutline }}
          placeholder="John Doe"
        />
      </FieldGroup>

      <AnimatePresence>
        {!!debounced.name && (
          <motion.div {...slideMotionProps}>
            <FieldGroup
              size="sm"
              label={<span className="text-caption-sm text-text-tertiary">{t('creator.terms.dob')}</span>}
            >
              <Controller
                control={control}
                name="date_of_birth"
                rules={{
                  required: t('validation.required'),
                  validate: value => {
                    if (new Date(value) < DATES.min || new Date(value) > DATES.max) {
                      return t('validation.invalid_date')
                    }
                  },
                }}
                render={({ field: { value, onChange, ...rest } }) => (
                  <InputDate
                    {...rest}
                    extraLeft={{ icon: BirthdayCakeOutline }}
                    value={value ? new Date(value) : null}
                    onChange={async v => {
                      onChange(v)
                      await trigger('date_of_birth')
                    }}
                    onInvalid={() => {
                      setError('date_of_birth', { message: t('validation.invalid_date') })
                    }}
                    calendar={{
                      disabled: {
                        before: DATES.min,
                        after: DATES.max,
                      },
                      defaultMonth: dayjs().subtract(18, 'years').toDate(),
                    }}
                  />
                )}
              />
              <ErrorMessage
                name="date_of_birth"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FieldGroup>

            <FieldGroup
              size="sm"
              label={<span className="text-caption-sm text-text-tertiary">{t('creator.terms.country')}</span>}
            >
              <SelectJurisdiction
                value={{ country, state }}
                onChange={v => {
                  setValue('country', v.country)
                  setValue('state', v.state)
                }}
              />
            </FieldGroup>

            {fulfilled && (
              <motion.div {...slideMotionProps}>
                <Confirmbox
                  dob={dayjs(dob).toDate()}
                  country={country}
                  state={state}
                  disabled={isSubmitting}
                  checked={confirmed}
                  onChange={() => setConfirmed(!confirmed)}
                />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="my-[18px] divider" />

      <div className="flex justify-end gap-3">
        <Button color="primary" type="submit" disabled={submitDisabled} loading={isSubmitting}>
          {t('creator.terms.accept')}
        </Button>
      </div>
    </form>
  )
}

interface ConfirmboxProps extends ComponentProps<typeof Checkbox> {
  dob: Date
  country: Country
  state?: UnitedStates
}

function Confirmbox({ dob, country, state, ...rest }: ConfirmboxProps) {
  const { token } = useCreatorToken()
  const { isMinor, isLoading, isError } = useCreatorLegality(token, dob, country, state)

  switch (true) {
    case isLoading:
      return <Skeleton className="h-8" />
    case isError:
      return (
        <Callout variant="danger">
          <Trans i18nKey="creator.terms.confirm.error" components={{ a: <a className="font-semibold" /> }} />
        </Callout>
      )
    case !!isMinor:
      return (
        <div className="flex flex-row gap-3">
          <Checkbox {...rest} />
          <Trans
            i18nKey="creator.terms.confirm-minor"
            components={{
              span: <span className="text-caption-sm font-normal text-text-secondary" />,
              a: <a href={CREATOR_PROGRAM_TERMS} target="_blank" className="text-text-brand-primary" />,
              a2: <a href={CREATOR_PROGRAM_FOR_MINORS_TERMS} target="_blank" className="text-text-brand-primary" />,
            }}
          />
        </div>
      )
    default:
      return (
        <div className="flex flex-row gap-3">
          <Checkbox {...rest} />
          <Trans
            i18nKey="creator.terms.confirm"
            components={{
              span: <span className="text-caption-sm font-normal text-text-secondary" />,
              a: <a href={CREATOR_PROGRAM_TERMS} target="_blank" className="text-text-brand-primary" />,
            }}
          />
        </div>
      )
  }
}
